import { APPLICATIONS, applicationLabel } from '../../assets/utils/accessCatalog';

/**
 * Which portal's permissions belong in a role editor.
 *
 * A role is issued to one kind of account — rbac_roles.user_type — and that
 * account only ever signs in to one portal. Offering an admin role the
 * facility portal's permissions is not just noise: facilities.read_own and
 * tariffs.read_own are scoped to "the facility you belong to", which a staff
 * account does not have, so granting them to an admin role means nothing.
 *
 * `shared` is always in scope. Those permissions are genuinely used from more
 * than one app (or are API-only), so excluding them would hide real
 * capabilities — claims.create and preauth.create are held by claims_manager
 * and medical_officer as readily as by a facility.
 *
 * Nothing here is enforcement. It decides what a human is shown by default;
 * the server decides what a permission does.
 */

/** rbac_roles.user_type -> the applications that account type can reach. */
export const PORTAL_BY_USER_TYPE = {
  staff: ['care-admin', 'shared'],
  admin: ['care-admin', 'shared'],
  facility: ['facility', 'shared'],
  client: ['enrollee', 'shared'],
  corporation: ['enrollee', 'shared'],
};

export const ALL_APPLICATION_KEYS = APPLICATIONS.map((a) => a.key);

/**
 * @returns the application keys in scope for a role, or every key when the
 *   role's user_type is unknown — an unrecognised type must not produce an
 *   empty editor, which would read as "this role can be given nothing".
 */
export const applicationsForUserType = (userType) => {
  const key = String(userType || '').toLowerCase();
  return PORTAL_BY_USER_TYPE[key] || ALL_APPLICATION_KEYS;
};

/** True when the role's type maps to a known, narrower-than-everything scope. */
export const hasKnownPortal = (userType) =>
  Object.prototype.hasOwnProperty.call(PORTAL_BY_USER_TYPE, String(userType || '').toLowerCase());

/**
 * The portal a role belongs to, named for a human.
 *
 * staff and admin both live in Care Admin, so the label follows the
 * application rather than the user_type — "Staff (Care Admin)" and
 * "Admin (Care Admin)" would read as two different portals.
 */
export const portalLabelForUserType = (userType) => {
  const apps = applicationsForUserType(userType).filter((a) => a !== 'shared');
  if (!apps.length) return 'All portals';
  return apps.map(applicationLabel).join(' / ');
};

/**
 * @param permissions enriched permissions carrying `application`
 * @param scope       application keys to keep
 */
export const filterPermissionsByApplication = (permissions = [], scope = []) => {
  const allowed = new Set(scope);
  if (!allowed.size) return permissions;
  return (permissions || []).filter((p) => allowed.has(p?.application));
};

/**
 * Permissions already on the role that fall outside its portal scope.
 *
 * These must stay visible however the filter is set: hiding an attached
 * permission would let a save silently drop it, and it is also the only
 * signal that something was mis-assigned in the first place.
 */
export const outOfScopeAssigned = (permissions = [], scope = [], assignedSet = new Set()) => {
  const allowed = new Set(scope);
  if (!allowed.size) return [];
  return (permissions || []).filter(
    (p) => assignedSet.has(String(p?.id ?? p?.permission_id ?? '')) && !allowed.has(p?.application)
  );
};
